/**
 * Batches invalidations (T14-08): every key a message maps to waits `debounceMs` (250 ms) for
 * more messages, then **one** `invalidateQueries` with a predicate covering all of them — 20
 * messages about the same order = 1 refetch of each query they touch. A steady flood still
 * flushes after `maxWaitMs` (1 s) so the screen is never more than ≤ 2 s behind (R21).
 */
import { hashKey, partialMatchKey, type QueryClient } from "@tanstack/react-query";

import type { ChangeMessage } from "../sse";
import { createQueryKeyMap, type KeyMap, type QueryKey } from "./keys";

export interface QueryBridgeOptions {
  keyMap?: KeyMap;
  debounceMs?: number;
  maxWaitMs?: number;
}

export interface QueryBridge {
  /** Invalidate the keys this message maps to (batched). */
  handle(message: ChangeMessage): void;
  /** Invalidate these keys (batched); `[]` = every query. */
  invalidate(keys: readonly QueryKey[]): void;
  /** Run the pending invalidation now. */
  flush(): Promise<void>;
  readonly keyMap: KeyMap;
  dispose(): void;
}

export function createQueryBridge(
  queryClient: QueryClient,
  options: QueryBridgeOptions = {},
): QueryBridge {
  const keyMap = options.keyMap ?? createQueryKeyMap();
  const debounceMs = options.debounceMs ?? 250;
  const maxWaitMs = Math.max(options.maxWaitMs ?? 1_000, debounceMs);
  const pending = new Map<string, QueryKey>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let firstAt = 0;

  function flush(): Promise<void> {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (pending.size === 0) {
      return Promise.resolve();
    }
    const keys = [...pending.values()];
    pending.clear();
    return queryClient.invalidateQueries({
      predicate: (query) => keys.some((key) => partialMatchKey(query.queryKey, key)),
    });
  }

  function arm(): void {
    const now = Date.now();
    if (timer === null) {
      firstAt = now;
    } else {
      clearTimeout(timer);
    }
    const wait = Math.max(0, Math.min(debounceMs, firstAt + maxWaitMs - now));
    timer = setTimeout(() => {
      void flush();
    }, wait);
  }

  function invalidate(keys: readonly QueryKey[]): void {
    if (keys.length === 0) {
      pending.set("[]", []);
    }
    for (const key of keys) {
      pending.set(hashKey(key), key);
    }
    arm();
  }

  return {
    keyMap,
    handle(message) {
      const keys = keyMap(message);
      if (keys.length > 0) {
        invalidate(keys);
      }
    },
    invalidate,
    flush,
    dispose() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pending.clear();
    },
  };
}

const bridges = new WeakMap<QueryClient, QueryBridge>();

/** The bridge of this QueryClient (created with `options` the first time). */
export function bridgeFor(queryClient: QueryClient, options?: QueryBridgeOptions): QueryBridge {
  let bridge = bridges.get(queryClient);
  if (!bridge) {
    bridge = createQueryBridge(queryClient, options);
    bridges.set(queryClient, bridge);
  }
  return bridge;
}
